import React, { ComponentType, LazyExoticComponent } from 'react';

/**
 * Module Registry
 *
 * Maps a logical component path (`patient/PatientDmInfoEdit`) to a base loader
 * plus optional per-tenant overrides, so a tenant can swap one screen without
 * the core router knowing that tenant exists.
 *
 * Resolution is a pure function of (path, tenant) -- the registry holds no
 * "current tenant". Callers pass the tenant they are rendering for, which keeps
 * the registry safe to read from concurrent React renders.
 */

type ComponentLoader = () => Promise<{ default: ComponentType<unknown> }>;

interface ModuleComponent {
  base?: ComponentLoader;
  tenantOverrides: Record<string, ComponentLoader>;
}

/** Fallback tenant key used when a caller has no tenant in scope. */
export const DEFAULT_TENANT_KEY = 'default';

class ModuleRegistry {
  private components: Map<string, ModuleComponent> = new Map();

  /** React.lazy is memoised per (path, tenant) so re-renders reuse one component. */
  private lazyCache: Map<string, LazyExoticComponent<ComponentType<unknown>>> = new Map();

  private entry(path: string): ModuleComponent {
    let existing = this.components.get(path);
    if (!existing) {
      existing = { tenantOverrides: {} };
      this.components.set(path, existing);
    }
    return existing;
  }

  /** Register the default implementation for a component path. */
  registerComponent(path: string, loader: ComponentLoader): void {
    this.entry(path).base = loader;
    this.invalidate(path);
  }

  /** Register a tenant-specific implementation, overriding the base for that tenant. */
  registerTenantOverride(path: string, tenant: string, loader: ComponentLoader): void {
    this.entry(path).tenantOverrides[tenant] = loader;
    this.invalidate(path);
  }

  /** Resolve the loader for a path, preferring the tenant override. */
  private resolveLoader(path: string, tenant: string): ComponentLoader | undefined {
    const component = this.components.get(path);
    if (!component) return undefined;
    return component.tenantOverrides[tenant] ?? component.base;
  }

  /**
   * Get the component registered at `path` for `tenant`.
   * Returns `null` when nothing is registered, so callers can fall back
   * instead of rendering an error string.
   */
  getComponent(path: string, tenant: string): LazyExoticComponent<ComponentType<unknown>> | null {
    const loader = this.resolveLoader(path, tenant);
    if (!loader) return null;

    const cacheKey = `${tenant}::${path}`;
    const cached = this.lazyCache.get(cacheKey);
    if (cached) return cached;

    const lazy = React.lazy(loader);
    this.lazyCache.set(cacheKey, lazy);
    return lazy;
  }

  /** True when `tenant` has its own implementation of `path`. */
  hasTenantOverride(path: string, tenant: string): boolean {
    return !!this.components.get(path)?.tenantOverrides[tenant];
  }

  /** True when anything (base or override) can serve `path` for `tenant`. */
  isRegistered(path: string, tenant: string): boolean {
    return !!this.resolveLoader(path, tenant);
  }

  getRegisteredPaths(): string[] {
    return Array.from(this.components.keys());
  }

  private invalidate(path: string): void {
    for (const key of Array.from(this.lazyCache.keys())) {
      if (key.endsWith(`::${path}`)) this.lazyCache.delete(key);
    }
  }
}

// Singleton instance
export const moduleRegistry = new ModuleRegistry();

// Helper function to create module path
export const modulePath = (module: string, component: string): string => {
  return `${module}/${component}`;
};

export default moduleRegistry;
